import { RefObject } from 'react';

import { useIntersectionCallbacks } from '@/hooks/useIntersectionCallbacks';

export type UseViewportVideosPlaybackOptions = Pick<
	IntersectionObserverInit,
	'root' | 'threshold'
> & {
	scrollMargin?: string;
};

const videoHasSource = (video: HTMLVideoElement) =>
	Boolean(video.currentSrc) ||
	Boolean(video.getAttribute('src')) ||
	Boolean(video.querySelector('source[src]'));

const pendingPlayCleanups = new WeakMap<HTMLVideoElement, () => void>();

const clearPendingPlay = (video: HTMLVideoElement) => {
	pendingPlayCleanups.get(video)?.();
	pendingPlayCleanups.delete(video);
};

const tryPlay = (video: HTMLVideoElement) => {
	video.play().catch(() => {});
};

const playWhenReady = (video: HTMLVideoElement) => {
	if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
		tryPlay(video);
		return;
	}

	const onReady = () => tryPlay(video);
	video.addEventListener('canplay', onReady, { once: true });

	const prev = pendingPlayCleanups.get(video);
	pendingPlayCleanups.set(video, () => {
		prev?.();
		video.removeEventListener('canplay', onReady);
	});
};

const playVideosIn = (root: Element) => {
	root.querySelectorAll<HTMLVideoElement>('video').forEach(video => {
		clearPendingPlay(video);

		if (videoHasSource(video)) {
			if (!video.currentSrc && video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();

			playWhenReady(video);
			return;
		}

		const observer = new MutationObserver(() => {
			if (!videoHasSource(video)) return;
			observer.disconnect();
			pendingPlayCleanups.delete(video);
			video.load();
			playWhenReady(video);
		});

		observer.observe(video, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['src']
		});

		pendingPlayCleanups.set(video, () => observer.disconnect());
	});
};

const pauseVideosIn = (root: Element) => {
	root.querySelectorAll<HTMLVideoElement>('video').forEach(video => {
		clearPendingPlay(video);
		video.pause();
	});
};

export const useViewportVideosPlayback = (
	refs: ReadonlyArray<RefObject<Element | null>>,
	options?: UseViewportVideosPlaybackOptions
) => {
	const { root, threshold = 0, scrollMargin } = options ?? {};

	useIntersectionCallbacks(refs, {
		onEntry: entry => playVideosIn(entry.target),
		onLeave: entry => pauseVideosIn(entry.target),
		root,
		scrollMargin,
		threshold
	});
};
